import React, { useState, useEffect } from 'react';

export const TrustCenter = ({ onBackClick }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [highlightAnimated, setHighlightAnimated] = useState(false);

  useEffect(() => {
    // Trigger highlight animation after component mounts
    const timer = setTimeout(() => {
      setHighlightAnimated(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Security sections data from Hedy.ai Trust Center
  const securitySections = [
    {
      id: 'encryption',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      ),
      title: 'Encryption & Data Protection',
      description: 'Data encryption at rest and in transit',
      count: 7,
      items: [
        { title: 'Encryption at Rest', description: 'All data stored in cloud infrastructure is encrypted at rest using industry-standard encryption algorithms' },
        { title: 'Encryption in Transit', description: 'All data transmitted over networks uses TLS/SSL encryption with strong cipher suites' },
        { title: 'Encryption and Key Management Policy', description: 'Comprehensive encryption and key management policies are documented and implemented' },
        { title: 'Remote Access Encryption', description: 'All remote access connections are encrypted and secured' },
        { title: 'Key Management', description: 'Encryption keys are managed securely with proper rotation and access controls' },
        { title: 'Database SSL/TLS', description: 'Database connections require SSL/TLS encryption for all incoming connections' },
        { title: 'Load Balancer HTTPS', description: 'Load balancers are configured to use HTTPS for secure communication' }
      ]
    },
    {
      id: 'access',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      title: 'Access Control & Authentication',
      description: 'Identity and access management controls',
      count: 14,
      items: [
        { title: 'Multi-Factor Authentication', description: 'MFA is enforced for all user accounts, administrative access, and developer tools' },
        { title: 'Strong Password Requirements', description: 'Strong password policies are enforced across all systems, services, and user endpoints' },
        { title: 'Least Privilege Access', description: 'Users and service accounts are granted minimum necessary permissions following least privilege principles' },
        { title: 'Session Timeout', description: 'Automatic session timeout is configured for cloud service providers and user endpoints' },
        { title: 'Service Account Security', description: 'Service accounts are configured without excessive privileges, avoid admin access, and use secure authentication' },
        { title: 'Service Account Key Rotation', description: 'Service account keys are regularly rotated and monitored for age' },
        { title: 'Unique SSH Keys', description: 'Unique SSH keys are enforced for cloud provider access, blocking project-wide SSH keys' },
        { title: 'Separation of Duties', description: 'Separation of duties is enforced for critical roles, permissions, and KMS-related roles' },
        { title: 'Access Reviews', description: 'Regular reviews of user access and permissions are conducted to ensure appropriate access levels' },
        { title: 'User Access Tracking', description: 'All user access activities are tracked and logged for audit purposes' },
        { title: 'Access Control and Termination Policy', description: 'Policies for access control and termination are documented and enforced' },
        { title: 'Role-Based Access Control Matrix', description: 'Role-based access control matrix is maintained and reviewed regularly' },
        { title: 'Default Account Restrictions', description: 'Default service accounts and root user accounts are restricted from full access' },
        { title: 'IAM Role Change Monitoring', description: 'Changes to IAM roles and custom roles are monitored and alerted' }
      ]
    },
    {
      id: 'monitoring',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
          <line x1="8" y1="21" x2="16" y2="21"/>
          <line x1="12" y1="17" x2="12" y2="21"/>
        </svg>
      ),
      title: 'Monitoring & Logging',
      description: 'Security monitoring and audit logging',
      count: 18,
      items: [
        { title: 'Threat Detection', description: 'Comprehensive threat detection capabilities are implemented for cloud infrastructure' },
        { title: 'Security Monitoring', description: 'Continuous monitoring for security-related events, anomalies, and infrastructure health' },
        { title: 'Audit Logging', description: 'Comprehensive audit logging is enabled for all critical operations and configuration changes' },
        { title: 'Log Retention', description: 'Logs are retained for appropriate periods to support security investigations and compliance' },
        { title: 'Network Flow Logging', description: 'Network flow logs are enabled for all subnets to track network traffic and detect anomalies' },
        { title: 'Centralized Logging', description: 'Log sinks are configured to centralize log collection and analysis' },
        { title: 'Security Alerting', description: 'Automated alerts are configured for critical security events and configuration changes' },
        { title: 'Change Tracking', description: 'All system changes are tracked and logged for audit purposes' },
        { title: 'Web Application Logging', description: 'Comprehensive logging is implemented for web applications to track security events' },
        { title: 'Web Application Monitoring', description: 'Continuous monitoring is implemented for web applications to detect security issues' },
        { title: 'Network Route Change Alerts', description: 'Alerts are configured for VPC network route changes' },
        { title: 'Firewall Rule Change Alerts', description: 'Alerts are configured for VPC network firewall rule changes' },
        { title: 'VPC Network Change Alerts', description: 'Alerts are configured for VPC network changes' },
        { title: 'SQL Configuration Change Alerts', description: 'Alerts are configured for SQL instance configuration changes' },
        { title: 'Storage IAM Change Alerts', description: 'Alerts are configured for cloud storage IAM permission changes' },
        { title: 'Audit Configuration Change Alerts', description: 'Alerts are configured for audit configuration changes' },
        { title: 'Project Ownership Change Alerts', description: 'Alerts are configured for project ownership assignment changes' },
        { title: 'Container Cluster Monitoring', description: 'Monitoring is enabled for container clusters to track security and performance' }
      ]
    },
    {
      id: 'network',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
          <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
      ),
      title: 'Network Security',
      description: 'Network segmentation and firewall controls',
      count: 11,
      items: [
        { title: 'Private Network Configuration', description: 'Compute clusters are configured with private network access only' },
        { title: 'Firewall Configuration', description: 'Firewall rules are properly configured, documented, and not excessively permissive' },
        { title: 'Firewall Rule Optimization', description: 'Firewall rules are optimized to prevent excessive or redundant rules' },
        { title: 'Restricted SSH Access', description: 'SSH access from the internet is blocked or restricted to authorized sources only' },
        { title: 'Load Balancer Security', description: 'Load balancers are configured with HTTPS, Cloud Armor, and security features enabled' },
        { title: 'Network Policies', description: 'Network policies are implemented to control traffic between resources in container clusters' },
        { title: 'Private Access Configuration', description: 'Resources are configured to use private access where applicable to reduce exposure' },
        { title: 'IP Forwarding Restrictions', description: 'IP forwarding is disabled on instances to prevent unauthorized routing' },
        { title: 'Network Security Policy', description: 'Network security policies define firewall and network access controls' },
        { title: 'CDN Configuration', description: 'Content delivery networks are configured with security best practices' },
        { title: 'Architecture and Network Diagram', description: 'Architecture and network diagrams are maintained and updated regularly' }
      ]
    },
    {
      id: 'infrastructure',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
          <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
          <line x1="6" y1="6" x2="6.01" y2="6"/>
          <line x1="6" y1="18" x2="6.01" y2="18"/>
        </svg>
      ),
      title: 'Infrastructure Security',
      description: 'Compute and infrastructure security controls',
      count: 14,
      items: [
        { title: 'Asset Inventory', description: 'Comprehensive inventory of cloud infrastructure assets is maintained and updated' },
        { title: 'Asset Ownership', description: 'Cloud infrastructure assets have assigned owners responsible for their security' },
        { title: 'Container Security', description: 'Container-optimized operating systems are used for containerized workloads' },
        { title: 'Automatic Updates', description: 'Automatic security updates and patches are enabled for compute resources and container nodes' },
        { title: 'Automatic Node Repair', description: 'Automatic repair is enabled for compute nodes to maintain availability and security' },
        { title: 'Instance Security', description: 'Compute instances are configured with security best practices and hardened configurations' },
        { title: 'Instance Threshold Monitoring', description: 'Compute instance counts are monitored against defined thresholds' },
        { title: 'Resource Limit Monitoring', description: 'Resource account limits are monitored to prevent resource exhaustion' },
        { title: 'Serial Port Restrictions', description: 'Serial port access is disabled on compute instances' },
        { title: 'Project-Wide SSH Key Restrictions', description: 'Project-wide SSH keys are blocked to enforce individual key management' },
        { title: 'Multi-Region Deployment', description: 'Critical resources are deployed across multiple regions for resilience and availability' },
        { title: 'Instance Group Autoscaling', description: 'Instance groups are configured with autoscaling for availability and performance' },
        { title: 'Baseline Configurations', description: 'Baseline security configurations are defined and enforced for infrastructure components' },
        { title: 'Configuration and Asset Management Policy', description: 'Policies for configuration and asset management are documented and enforced' }
      ]
    },
    {
      id: 'code',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <polyline points="16 18 22 12 16 6"/>
          <polyline points="8 6 2 12 8 18"/>
        </svg>
      ),
      title: 'Code Security',
      description: 'Software development and code security',
      count: 12,
      items: [
        { title: 'Static Application Security Testing', description: 'SAST tools are used to identify security vulnerabilities in source code before merging' },
        { title: 'Code Change Tracking', description: 'All code changes are tracked and logged in version control systems' },
        { title: 'Integration Testing', description: 'Integration tests are performed to validate code functionality and security' },
        { title: 'Pull Request Approvals', description: 'Code changes require independent approval before being merged to main branches' },
        { title: 'Branch Protection', description: 'Branch protection rules are configured to enforce code review requirements' },
        { title: 'Repository Inventory', description: 'Complete inventory of version control repositories is maintained' },
        { title: 'Repository Ownership', description: 'Owners are assigned to production version control repositories' },
        { title: 'Version Control System', description: 'Version control system is properly configured and validated' },
        { title: 'Secure Development Policy', description: 'Secure development policies define secure coding practices and requirements' },
        { title: 'Pull Request Templates', description: 'Pull request templates are used to ensure consistent code review processes' },
        { title: 'Test Data Sanitization', description: 'Test data is sanitized to prevent exposure of sensitive information' },
        { title: 'Environment Segregation', description: 'Production, development, and staging environments are segregated' }
      ]
    },
    {
      id: 'backup',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <ellipse cx="12" cy="5" rx="9" ry="3"/>
          <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
        </svg>
      ),
      title: 'Backup & Recovery',
      description: 'Data backup and disaster recovery',
      count: 8,
      items: [
        { title: 'Automated Backups', description: 'Automated backups are configured for all critical databases and data stores' },
        { title: 'Backup Retention', description: 'Backup retention policies ensure data can be recovered from appropriate time periods' },
        { title: 'High Availability', description: 'High availability configurations are implemented for critical services' },
        { title: 'Object Versioning', description: 'Object versioning is enabled for storage buckets to prevent data loss' },
        { title: 'Backup Restoration Testing', description: 'Regular testing of backup restoration procedures is conducted' },
        { title: 'Business Continuity and Disaster Recovery Plan', description: 'Business continuity and disaster recovery plans are documented and tested' },
        { title: 'Disaster Recovery Tabletop Exercises', description: 'Disaster recovery tabletop exercises are conducted regularly' },
        { title: 'Storage Access Logging', description: 'Data access logging is enabled for storage buckets to track access patterns' }
      ]
    },
    {
      id: 'compliance',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
      ),
      title: 'Compliance & Policies',
      description: 'Security policies and compliance controls',
      count: 22,
      items: [
        { title: 'Security Policies', description: 'Comprehensive security policies are documented, reviewed, updated regularly, and have assigned owners' },
        { title: 'Information Security Policy', description: 'Information security policy defines security objectives and requirements' },
        { title: 'Access Control Policy', description: 'Access control and termination policies are documented and enforced' },
        { title: 'Acceptable Use Policy', description: 'Acceptable use policies are documented and enforced' },
        { title: 'Data Classification Policy', description: 'Data classification policies define how data is categorized and protected' },
        { title: 'Encryption Policy', description: 'Encryption and key management policies are documented and implemented' },
        { title: 'Network Security Policy', description: 'Network security policies define firewall and network access controls' },
        { title: 'Vulnerability Management Policy', description: 'Vulnerability and patch management policies ensure timely security updates' },
        { title: 'Change Management Policy', description: 'Change management policies define processes for system changes' },
        { title: 'Incident Response Plan', description: 'Security incident response plan is documented and tested regularly' },
        { title: 'Incident Response Tabletop Exercises', description: 'Security incident response tabletop exercises are conducted regularly' },
        { title: 'Incident Lessons Learned', description: 'Lessons learned from security incidents are documented and incorporated into improvements' },
        { title: 'Incident Notification Process', description: 'Process for security incident notification is documented and followed' },
        { title: 'Risk Assessment', description: 'Regular risk assessments are conducted and documented' },
        { title: 'Risk Assessment and Treatment Policy', description: 'Risk assessment and treatment policies are documented and acknowledged by personnel' },
        { title: 'Centralized Risk Register', description: 'Centralized risk register tracks and manages security risks' },
        { title: 'Vulnerability and Threat Tracking', description: 'Vulnerabilities and threats are tracked and managed through remediation' },
        { title: 'Statutory, Regulatory, and Contractual Compliance', description: 'Compliance with statutory, regulatory, and contractual requirements is maintained' },
        { title: 'Internal Control Policy', description: 'Internal control policies define control objectives and requirements' },
        { title: 'Internal Control Monitoring', description: 'Design and operating effectiveness of internal controls are continuously monitored' },
        { title: 'System Description', description: 'System description documents the control environment and how trust service criteria are met' },
        { title: 'Security Control Owners', description: 'Security controls have assigned owners responsible for their effectiveness' }
      ]
    },
    {
      id: 'privacy',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      ),
      title: 'Data Privacy & Protection',
      description: 'Data privacy and protection controls',
      count: 13,
      items: [
        { title: 'Privacy and Data Protection Policy', description: 'Privacy and data protection policies are documented and maintained' },
        { title: 'Privacy Policy on Website', description: 'Privacy policy is published on the organization\'s website' },
        { title: 'Privacy Policy Update History', description: 'Privacy policy maintains update history to track changes over time' },
        { title: 'Data Retention and Disposal Policy', description: 'Data retention and disposal policies define how long data is retained and how it is disposed' },
        { title: 'Customer Data Deletion and Retention', description: 'Policies and processes for deletion and retention of customer data are documented' },
        { title: 'Data Processing Agreement', description: 'Data processing agreements or equivalents are in place with data processors' },
        { title: 'Data Subject Request Handling', description: 'Process for handling data subject requests is documented and implemented' },
        { title: 'Record of Processing Activities', description: 'Record of processing activities is maintained to document data processing' },
        { title: 'Data Flow Diagram', description: 'Data flow diagrams document how data flows through systems' },
        { title: 'Data Collection Consent Forms', description: 'Data collection consent forms are used where required' },
        { title: 'Data Loss Prevention', description: 'Data loss prevention measures are implemented to prevent unauthorized data exfiltration' },
        { title: 'Processing Integrity Policy', description: 'Processing integrity policies ensure data processing accuracy and completeness' },
        { title: 'Privacy Council Meetings', description: 'Privacy council meetings are conducted to review privacy practices' }
      ]
    },
    {
      id: 'vendor',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="8.5" cy="7" r="4"/>
          <path d="M20 8v6"/>
          <path d="M23 11h-6"/>
        </svg>
      ),
      title: 'Vendor Management',
      description: 'Third-party vendor security controls',
      count: 8,
      items: [
        { title: 'Vendor Management Policy', description: 'Vendor management policies define requirements for third-party vendors' },
        { title: 'Vendor Confidentiality and Security Requirements', description: 'Vendor confidentiality and security requirements are documented in agreements' },
        { title: 'Vendor Risk Level Assessments', description: 'Vendor risk level assessments are conducted to evaluate vendor security' },
        { title: 'Vendor Security Reviews', description: 'Regular security reviews are conducted for vendors' },
        { title: 'Vendor Compliance Reports', description: 'Vendor compliance reports are obtained and reviewed' },
        { title: 'Vendor Owners', description: 'Vendors have assigned owners responsible for vendor relationship management' },
        { title: 'Confidentiality Requirements in Agreements', description: 'Confidentiality requirements are included in vendor and partner agreements' },
        { title: 'Customer Confidentiality and Security Requirements', description: 'Customer confidentiality and security requirements are documented in agreements' }
      ]
    },
    {
      id: 'secops',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
      ),
      title: 'Security Operations',
      description: 'Security operations and personnel controls',
      count: 16,
      items: [
        { title: 'Information Security Team', description: 'Information security team is established with defined roles and responsibilities' },
        { title: 'Information Security Manager Role', description: 'Information security manager role is defined and assigned' },
        { title: 'Security Team Meetings', description: 'Security team meetings are conducted regularly' },
        { title: 'Security Awareness Training', description: 'Annual security awareness training is provided to all personnel' },
        { title: 'Security Awareness Training at Hire', description: 'Security awareness training is provided to new hires' },
        { title: 'New Hire Screening', description: 'New hire screening processes verify candidate qualifications and background' },
        { title: 'Job Descriptions', description: 'Job descriptions define roles, responsibilities, and requirements' },
        { title: 'Job Candidate Resume Review', description: 'Job candidate resumes are reviewed to assess qualifications' },
        { title: 'Employee Confidentiality Agreements', description: 'Employee confidentiality agreements are signed by all personnel' },
        { title: 'Code of Conduct', description: 'Code of conduct defines expected behavior and ethical standards' },
        { title: 'Disciplinary Process', description: 'Disciplinary process is documented for policy violations' },
        { title: 'Performance Review Policy', description: 'Performance review policies define evaluation processes' },
        { title: 'Organizational Chart', description: 'Organizational chart documents organizational structure' },
        { title: 'Organizational Chart Distribution', description: 'Organizational chart is distributed to relevant personnel' },
        { title: 'Board of Directors Meeting Minutes', description: 'Board of directors meeting minutes are maintained' },
        { title: 'Cybersecurity Insurance', description: 'Cybersecurity insurance coverage is maintained' }
      ]
    },
    {
      id: 'physical',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
      title: 'Physical Security',
      description: 'Physical security controls',
      count: 6,
      items: [
        { title: 'Physical Security Policy', description: 'Physical security policies define physical access controls and requirements' },
        { title: 'Bring Your Own Computer Policy', description: 'BYOC policies define requirements for personal device usage' },
        { title: 'Removable Media Usage', description: 'Policies for removable media usage are documented and enforced' },
        { title: 'User Endpoint Inventory', description: 'Inventory of user endpoints is maintained' },
        { title: 'User Endpoint Ownership', description: 'User endpoints have assigned owners' },
        { title: 'Password Policy on User Endpoints', description: 'Password policies are enforced on user endpoints' }
      ]
    },
    {
      id: 'testing',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      ),
      title: 'Security Testing',
      description: 'Security testing and validation',
      count: 4,
      items: [
        { title: 'Penetration Testing', description: 'Regular penetration testing is conducted by qualified security professionals' },
        { title: 'External Vulnerability Scanning', description: 'External vulnerability scanning is conducted regularly' },
        { title: 'Application Processing Documentation', description: 'Application processing documentation describes system functionality' },
        { title: 'System Processing Documentation', description: 'System processing documentation describes system processes and design' }
      ]
    },
    {
      id: 'communications',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#385cbc" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      ),
      title: 'Communications & Transparency',
      description: 'External communications and transparency',
      count: 9,
      items: [
        { title: 'External Communications Channel', description: 'External communications channel is established for security inquiries' },
        { title: 'Internal Communications Channel', description: 'Internal communications channel is established for security matters' },
        { title: 'Website Security Commitments', description: 'Security commitments are published on the organization\'s website' },
        { title: 'Website Security Communication Channel', description: 'Security communication channel is published on the organization\'s website' },
        { title: 'Website Services and Products Page', description: 'Services and products description is published on the organization\'s website' },
        { title: 'Website Support Page', description: 'Support page is available on the organization\'s website' },
        { title: 'Career Page on Website', description: 'Career page is published on the organization\'s website' },
        { title: 'Terms of Service on Website', description: 'Terms of service are published on the organization\'s website' },
        { title: 'Terms of Service Update Date', description: 'Terms of service show date of last update' }
      ]
    }
  ];

  const totalControls = securitySections.reduce((sum, section) => sum + section.count, 0);

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f7f5f3',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
    >
      {/* Header */}
      <header
        style={{
          backgroundColor: '#ffffff',
          padding: '16px 40px',
          borderBottom: '1px solid #e5e5e5',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            onClick={onBackClick}
            style={{
              background: 'none',
              border: 'none',
              color: '#272726',
              fontSize: '15px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '500'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Real Patients
          </button>
          <span style={{ color: '#666', fontSize: '14px' }}>
            security@realpatients.com
          </span>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{ padding: '60px 40px 40px', position: 'relative', overflow: 'hidden' }}>
        {/* Decorative elements */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '10%',
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(56, 92, 188, 0.08) 0%, rgba(56, 92, 188, 0.02) 100%)',
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          bottom: '40px',
          left: '5%',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(241, 91, 65, 0.08) 0%, rgba(241, 91, 65, 0.02) 100%)',
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          top: '60%',
          right: '5%',
          width: '60px',
          height: '60px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, rgba(56, 92, 188, 0.06) 0%, rgba(56, 92, 188, 0.01) 100%)',
          transform: 'rotate(15deg)',
          zIndex: 0
        }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h1
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(32px, 5vw, 48px)',
              fontWeight: '700',
              color: '#272726',
              marginBottom: '24px',
              textAlign: 'center'
            }}
          >
            Real Patients{' '}
            <span style={{ position: 'relative', display: 'inline-block' }}>
              Trust
              <span
                style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '-4px',
                  right: '-4px',
                  height: '95%',
                  background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.5) 0%, rgba(245, 185, 165, 0.45) 40%, rgba(250, 200, 160, 0.4) 70%, rgba(245, 215, 170, 0.35) 100%)',
                  zIndex: -1,
                  transform: highlightAnimated ? 'scaleX(1)' : 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.6s ease-out',
                  borderRadius: '4px'
                }}
              />
            </span>{' '}
            Center
          </h1>
          <p
            style={{
              fontSize: '17px',
              lineHeight: '1.8',
              color: '#666',
              textAlign: 'left',
              marginBottom: '20px'
            }}
          >
            Real Patients provides a{' '}
            <span style={{ position: 'relative', display: 'inline' }}>
              secure platform
              <span style={{
                position: 'absolute',
                bottom: '0',
                left: '-2px',
                right: '-2px',
                height: '95%',
                background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.4) 0%, rgba(245, 185, 165, 0.35) 40%, rgba(250, 200, 160, 0.3) 70%, rgba(245, 215, 170, 0.25) 100%)',
                zIndex: -1,
                transform: highlightAnimated ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.6s ease-out 0.4s',
                borderRadius: '3px'
              }} />
            </span>{' '}
            for professionals who need to connect with patients without{' '}
            <span style={{ position: 'relative', display: 'inline' }}>
              compromising confidentiality
              <span style={{
                position: 'absolute',
                bottom: '0',
                left: '-2px',
                right: '-2px',
                height: '95%',
                background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.4) 0%, rgba(245, 185, 165, 0.35) 40%, rgba(250, 200, 160, 0.3) 70%, rgba(245, 215, 170, 0.25) 100%)',
                zIndex: -1,
                transform: highlightAnimated ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.6s ease-out 0.5s',
                borderRadius: '3px'
              }} />
            </span>
            . We process your data{' '}
            <span style={{ position: 'relative', display: 'inline' }}>
              securely
              <span style={{
                position: 'absolute',
                bottom: '0',
                left: '-2px',
                right: '-2px',
                height: '95%',
                background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.4) 0%, rgba(245, 185, 165, 0.35) 40%, rgba(250, 200, 160, 0.3) 70%, rgba(245, 215, 170, 0.25) 100%)',
                zIndex: -1,
                transform: highlightAnimated ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.6s ease-out 0.6s',
                borderRadius: '3px'
              }} />
            </span>
            , ensuring your conversations{' '}
            <span style={{ position: 'relative', display: 'inline' }}>
              remain private
              <span style={{
                position: 'absolute',
                bottom: '0',
                left: '-2px',
                right: '-2px',
                height: '95%',
                background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.4) 0%, rgba(245, 185, 165, 0.35) 40%, rgba(250, 200, 160, 0.3) 70%, rgba(245, 215, 170, 0.25) 100%)',
                zIndex: -1,
                transform: highlightAnimated ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.6s ease-out 0.7s',
                borderRadius: '3px'
              }} />
            </span>{' '}
            while delivering real-time insights.
          </p>
          <p
            style={{
              fontSize: '17px',
              lineHeight: '1.8',
              color: '#666',
              textAlign: 'left'
            }}
          >
            We understand that your professional conversations contain sensitive information that demands the{' '}
            <span style={{ position: 'relative', display: 'inline' }}>
              highest standards
              <span style={{
                position: 'absolute',
                bottom: '0',
                left: '-2px',
                right: '-2px',
                height: '95%',
                background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.4) 0%, rgba(245, 185, 165, 0.35) 40%, rgba(250, 200, 160, 0.3) 70%, rgba(245, 215, 170, 0.25) 100%)',
                zIndex: -1,
                transform: highlightAnimated ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.6s ease-out 0.8s',
                borderRadius: '3px'
              }} />
            </span>{' '}
            of data protection. That's why Real Patients is built with{' '}
            <span style={{ position: 'relative', display: 'inline' }}>
              privacy at its core
              <span style={{
                position: 'absolute',
                bottom: '0',
                left: '-2px',
                right: '-2px',
                height: '95%',
                background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.4) 0%, rgba(245, 185, 165, 0.35) 40%, rgba(250, 200, 160, 0.3) 70%, rgba(245, 215, 170, 0.25) 100%)',
                zIndex: -1,
                transform: highlightAnimated ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.6s ease-out 0.9s',
                borderRadius: '3px'
              }} />
            </span>
            —your data is{' '}
            <span style={{ position: 'relative', display: 'inline' }}>
              encrypted
              <span style={{
                position: 'absolute',
                bottom: '0',
                left: '-2px',
                right: '-2px',
                height: '95%',
                background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.4) 0%, rgba(245, 185, 165, 0.35) 40%, rgba(250, 200, 160, 0.3) 70%, rgba(245, 215, 170, 0.25) 100%)',
                zIndex: -1,
                transform: highlightAnimated ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.6s ease-out 1.0s',
                borderRadius: '3px'
              }} />
            </span>
            ,{' '}
            <span style={{ position: 'relative', display: 'inline' }}>
              protected
              <span style={{
                position: 'absolute',
                bottom: '0',
                left: '-2px',
                right: '-2px',
                height: '95%',
                background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.4) 0%, rgba(245, 185, 165, 0.35) 40%, rgba(250, 200, 160, 0.3) 70%, rgba(245, 215, 170, 0.25) 100%)',
                zIndex: -1,
                transform: highlightAnimated ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.6s ease-out 1.1s',
                borderRadius: '3px'
              }} />
            </span>
            , and{' '}
            <span style={{ position: 'relative', display: 'inline' }}>
              handled with the utmost care
              <span style={{
                position: 'absolute',
                bottom: '0',
                left: '-2px',
                right: '-2px',
                height: '95%',
                background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.4) 0%, rgba(245, 185, 165, 0.35) 40%, rgba(250, 200, 160, 0.3) 70%, rgba(245, 215, 170, 0.25) 100%)',
                zIndex: -1,
                transform: highlightAnimated ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.6s ease-out 1.2s',
                borderRadius: '3px'
              }} />
            </span>
            .
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 40px 80px' }}>

        {/* Compliance & Certifications Card */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '24px',
            border: '1px solid #e5e5e5'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#272726" strokeWidth="2">
              <circle cx="12" cy="8" r="7"/>
              <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/>
            </svg>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#272726', margin: 0, textAlign: 'left' }}>
              Compliance & Certifications
            </h2>
          </div>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px', textAlign: 'left' }}>
            Industry-standard certifications and compliance frameworks
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            {/* GDPR Compliant */}
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid #e5e5e5',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px'
              }}
            >
              <img
                src="/gdpr.jpeg"
                alt="GDPR"
                style={{
                  width: '64px',
                  height: '64px',
                  objectFit: 'contain',
                  flexShrink: 0
                }}
              />
              <div>
                <div style={{ fontWeight: '600', fontSize: '16px', color: '#272726', marginBottom: '8px' }}>
                  GDPR Compliant
                </div>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  backgroundColor: '#22c55e',
                  color: '#ffffff',
                  borderRadius: '100px',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Valid
                </span>
                <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                  European data protection and privacy compliance
                </div>
              </div>
            </div>

            {/* SOC 2 Type II */}
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid #e5e5e5',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px'
              }}
            >
              <img
                src="/soc2.png"
                alt="SOC 2"
                style={{
                  width: '64px',
                  height: '64px',
                  objectFit: 'contain',
                  flexShrink: 0
                }}
              />
              <div>
                <div style={{ fontWeight: '600', fontSize: '16px', color: '#272726', marginBottom: '8px' }}>
                  SOC 2 Type II
                </div>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  borderRadius: '100px',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Certification in Progress
                </span>
                <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                  Annual audit of security, confidentiality, and integrity
                </div>
              </div>
            </div>

            {/* HIPAA */}
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid #e5e5e5',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px'
              }}
            >
              <img
                src="/hipaa.jpeg"
                alt="HIPAA"
                style={{
                  width: '64px',
                  height: '64px',
                  objectFit: 'contain',
                  flexShrink: 0
                }}
              />
              <div>
                <div style={{ fontWeight: '600', fontSize: '16px', color: '#272726', marginBottom: '8px' }}>
                  HIPAA
                </div>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  borderRadius: '100px',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Certification in Progress
                </span>
                <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                  Healthcare data protection and privacy standards
                </div>
              </div>
            </div>

            {/* SSL Certificate */}
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid #e5e5e5',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px'
              }}
            >
              <img
                src="/ssl.jpeg"
                alt="SSL"
                style={{
                  width: '64px',
                  height: '64px',
                  objectFit: 'contain',
                  flexShrink: 0
                }}
              />
              <div>
                <div style={{ fontWeight: '600', fontSize: '16px', color: '#272726', marginBottom: '8px' }}>
                  SSL/TLS Encryption
                </div>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  backgroundColor: '#22c55e',
                  color: '#ffffff',
                  borderRadius: '100px',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Active
                </span>
                <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                  256-bit encryption for all data in transit
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resources & Documentation Card */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '24px',
            border: '1px solid #e5e5e5'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#272726" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#272726', margin: 0, textAlign: 'left' }}>
              Resources & Documentation
            </h2>
          </div>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px', textAlign: 'left' }}>
            Easily access documentation on HIPAA, DPA, compliance, and more.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            {[
              {
                title: 'Privacy Policy',
                description: 'English – Public privacy policy',
                public: true
              },
              {
                title: 'Terms of Service',
                description: 'English – Public terms of service',
                public: true
              },
              {
                title: 'Data Processing Addendum',
                description: 'Public data processing addendum',
                public: true
              },
              {
                title: 'HIPAA Business Associate Agreement',
                description: 'Requires access approval',
                public: false
              },
              {
                title: 'Security Whitepaper',
                description: 'Requires access approval',
                public: false
              },
              {
                title: 'SOC 2 Report',
                description: 'Requires access approval',
                public: false
              }
            ].map((doc, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '20px',
                  border: '1px solid #e5e5e5',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  <span style={{ fontSize: '15px', fontWeight: '600', color: '#272726', lineHeight: '1.4' }}>
                    {doc.title}
                  </span>
                </div>
                <div style={{
                  fontSize: '13px',
                  color: doc.public ? '#22c55e' : '#f59e0b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  {!doc.public && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  )}
                  {doc.description}
                </div>
                <button
                  style={{
                    marginTop: 'auto',
                    padding: '10px 16px',
                    backgroundColor: doc.public ? '#3b82f6' : '#ffffff',
                    color: doc.public ? '#ffffff' : '#666',
                    border: doc.public ? 'none' : '1px solid #e5e5e5',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {doc.public ? (
                      <>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </>
                    ) : (
                      <>
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </>
                    )}
                  </svg>
                  {doc.public ? 'View Document' : 'Request Access'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Subprocessors Card */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '24px',
            border: '1px solid #e5e5e5'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#272726" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#272726', margin: 0, textAlign: 'left' }}>
              Subprocessors
            </h2>
          </div>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px', textAlign: 'left' }}>
            The following organizations are used as subprocessors to help us deliver services and features.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            {/* Google Cloud Platform */}
            <div style={{
              backgroundColor: '#f7f5f3',
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px'
            }}>
              <img src="/google cloud.png" alt="Google Cloud Platform" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#272726' }}>Google Cloud Platform</span>
            </div>

            {/* Google Workspace */}
            <div style={{
              backgroundColor: '#f7f5f3',
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px'
            }}>
              <img src="/google workspace.png" alt="Google Workspace" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#272726' }}>Google Workspace</span>
            </div>

            {/* AWS */}
            <div style={{
              backgroundColor: '#f7f5f3',
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px'
            }}>
              <img src="/aws.jpeg" alt="AWS" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#272726' }}>AWS</span>
            </div>

            {/* Xano */}
            <div style={{
              backgroundColor: '#f7f5f3',
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px'
            }}>
              <img src="/xano.png" alt="Xano" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#272726' }}>Xano</span>
            </div>

            {/* HubSpot */}
            <div style={{
              backgroundColor: '#f7f5f3',
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px'
            }}>
              <img src="/hubspot.png" alt="HubSpot" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#272726' }}>HubSpot</span>
            </div>

            {/* Recollective */}
            <div style={{
              backgroundColor: '#f7f5f3',
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px'
            }}>
              <img src="/recollective.png" alt="Recollective" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#272726' }}>Recollective</span>
            </div>

            {/* SendGrid */}
            <div style={{
              backgroundColor: '#f7f5f3',
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px'
            }}>
              <img src="/sendgrid.jpeg" alt="SendGrid" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#272726' }}>SendGrid</span>
            </div>

            {/* Twilio */}
            <div style={{
              backgroundColor: '#f7f5f3',
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px'
            }}>
              <img src="/twilio.png" alt="Twilio" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#272726' }}>Twilio</span>
            </div>

            {/* Tremendous */}
            <div style={{
              backgroundColor: '#f7f5f3',
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px'
            }}>
              <img src="/tremendous.png" alt="Tremendous" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#272726' }}>Tremendous</span>
            </div>
          </div>
        </div>

        {/* Security Measures Card */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '24px',
            border: '1px solid #e5e5e5'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#272726" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#272726', margin: 0, textAlign: 'left' }}>
              Security Measures
            </h2>
          </div>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px', textAlign: 'left' }}>
            {totalControls}+ security controls across {securitySections.length} categories, continuously monitored.
          </p>

          {/* Security Sections Accordion */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {securitySections.map((section) => (
              <div
                key={section.id}
                style={{
                  backgroundColor: '#f8f8f8',
                  borderRadius: '12px',
                  border: '1px solid #e5e5e5',
                  overflow: 'hidden'
                }}
              >
                {/* Section Header */}
                <div
                  onClick={() => toggleSection(section.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 20px',
                    backgroundColor: '#f8f8f8',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: '#e3f2fd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {section.icon}
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: '600', fontSize: '15px', color: '#272726', textAlign: 'left' }}>
                        {section.title}
                      </div>
                      <div style={{ fontSize: '13px', color: '#888', textAlign: 'left' }}>
                        {section.description}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                      padding: '4px 12px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#272726'
                    }}>
                      {section.count}
                    </span>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#999"
                      strokeWidth="2"
                      style={{
                        transform: expandedSections[section.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </div>
                </div>

                {/* Expanded Items */}
                {expandedSections[section.id] && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    padding: '0 12px 12px 12px'
                  }}>
                    {section.items.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '16px',
                          padding: '16px 20px',
                          backgroundColor: '#ffffff',
                          borderRadius: '8px',
                          border: '1px solid #e5e5e5'
                        }}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          style={{ flexShrink: 0, marginTop: '2px' }}
                        >
                          <circle cx="12" cy="12" r="10" fill="#dcfce7" stroke="#22c55e" strokeWidth="2"/>
                          <path d="M9 12l2 2 4-4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <div style={{ textAlign: 'left', flex: 1 }}>
                          <div style={{ fontWeight: '600', fontSize: '15px', color: '#272726', marginBottom: '4px', textAlign: 'left' }}>
                            {item.title}
                          </div>
                          <div style={{ fontSize: '14px', color: '#888', lineHeight: '1.5', textAlign: 'left' }}>
                            {item.description}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Privacy & Data Protection Principles */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '24px',
            border: '1px solid #e5e5e5'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#272726" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#272726', margin: 0, textAlign: 'left' }}>
              Privacy & Data Protection
            </h2>
          </div>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px', textAlign: 'left' }}>
            Our commitment to protecting your data and privacy
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              {
                title: 'Informed Consent',
                description: 'Your data is only shared with our partners after you provide clear, informed consent. You always know how your information will be used before participating.',
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f15b41" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                )
              },
              {
                title: 'Anonymized Data',
                description: 'When your insights are part of our data platform, they are anonymized so they cannot be tied back to you as an individual. Your identity remains protected.',
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f15b41" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4"/>
                    <path d="M12 8h.01"/>
                  </svg>
                )
              },
              {
                title: 'Interview Privacy',
                description: 'For interviews, clients may see your video and name, but will not use any identifying information publicly unless you expressly provide additional consent.',
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f15b41" strokeWidth="2">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                )
              },
              {
                title: 'End-to-End Security',
                description: 'All sensitive data is encrypted both in transit and at rest using industry-standard encryption, protecting your conversations and health information.',
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f15b41" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2"/>
                    <path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                )
              },
              {
                title: 'User Control',
                description: 'You maintain full control over your data, including the ability to delete it at any time.',
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f15b41" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                )
              }
            ].map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px',
                  padding: '20px',
                  backgroundColor: '#f7f5f3',
                  borderRadius: '8px'
                }}
              >
                <div style={{ flexShrink: 0, marginTop: '2px' }}>
                  {item.icon}
                </div>
                <div style={{ textAlign: 'left', flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '15px', color: '#272726', marginBottom: '4px', textAlign: 'left' }}>
                    {item.title}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.5', textAlign: 'left' }}>
                    {item.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div
          style={{
            backgroundColor: '#272726',
            borderRadius: '12px',
            padding: '40px'
          }}
        >
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: '24px',
              fontWeight: '600',
              color: '#ffffff',
              marginBottom: '12px'
            }}
          >
            Security Contact
          </h2>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.7)', marginBottom: '24px' }}>
            Questions about our security practices? Our team is here to help.
          </p>
          <a
            href="mailto:security@realpatients.com"
            style={{
              display: 'inline-block',
              padding: '12px 28px',
              backgroundColor: '#f15b41',
              color: '#ffffff',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              textDecoration: 'none'
            }}
          >
            Contact Security Team
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ padding: '24px 40px', backgroundColor: '#272726', textAlign: 'center' }}>
        <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>
          © 2026 Real Patients. All rights reserved.
        </p>
      </footer>
    </div>
  );
};
